"use client";

import React from "react";
import { Ruler, Hammer, Wrench, Droplets, ShieldCheck, Quote, CircleCheck } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const processSteps = [{
  id: 1,
  title: "Measurement & Assessment",
  description: "We take precise measurements of your roofline and assess water flow patterns to ensure a perfect, custom fit for your home.",
  icon: Ruler
}, {
  id: 2,
  title: "Custom Fabrication",
  description: "Using our mobile rolling machines, we fabricate seamless gutters on-site to match your home's exact dimensions, eliminating weak points.",
  icon: Hammer
}, {
  id: 3,
  title: "Professional Installation",
  description: "Our expert team secures the gutters using heavy-duty hidden hangers, pitched perfectly to guarantee optimal water drainage.",
  icon: Wrench
}, {
  id: 4,
  title: "Testing & Cleanup",
  description: "We perform rigorous water testing to ensure zero leaks and conduct a thorough cleanup of your property before we consider the job done.",
  icon: Droplets
}];
const galleryImages = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_before_1.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_before_1.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_before_1.png-wvc-sizes",
  alt: "Old, sagging gutters before replacement",
  label: "Before"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_after_1.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_after_1.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_after_1.png-wvc-sizes",
  alt: "New seamless gutters installed on a modern home",
  label: "After"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_before_2.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_before_2.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_before_2.png-wvc-sizes",
  alt: "Clogged and overflowing gutters",
  label: "Before"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_after_2.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_after_2.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/gutter_after_2.webp-wvc-sizes",
  alt: "Clean, functional seamless gutters on a traditional home",
  label: "After"
}];
export default function ServiceDetailsInstallationProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: leftColRef,
    isVisible: isLeftColVisible
  } = useRevealOnScroll();
  const {
    ref: rightColRef,
    isVisible: isRightColVisible
  } = useRevealOnScroll();
  return <section id="service-details-installation-process" className="bg-background py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4">
            Our Process
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Seamless Gutter Installation
          </h2>
          <p className="text-muted-foreground text-lg font-sans">
            We follow a strict, proven methodology to ensure your new gutter system provides decades of reliable protection for your home.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          
          {}
          <div ref={leftColRef} className={`transition-all duration-700 delay-100 ease-out ${isLeftColVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <h3 className="text-2xl font-bold font-default text-foreground mb-8 flex items-center gap-2">
              <CircleCheck className="text-primary size-6" />
              Step-by-Step Installation
            </h3>
            
            <div className="relative border-l-2 border-muted ml-4 md:ml-6 space-y-10 pb-4">
              {processSteps.map((step, i) => <div key={step.id} data-index={i} className="relative pl-8 md:pl-10">
                  {}
                  <div className="absolute -left-[21px] top-0 bg-background border-2 border-primary rounded-lg p-2 shadow-sm">
                    <step.icon className="size-5 text-primary" />
                  </div>
                  
                  {}
                  <div className="bg-card border border-border/50 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow">
                    <h4 className="text-xl font-semibold font-default text-foreground mb-2">
                      {step.id}. {step.title}
                    </h4>
                    <p className="text-muted-foreground font-sans leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div ref={rightColRef} className={`space-y-8 transition-all duration-700 delay-200 ease-out ${isRightColVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            
            {}
            <Card className="bg-primary/5 border-primary/20 shadow-sm">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                  <ShieldCheck className="size-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-default text-foreground mb-2">
                    10-Year Comprehensive Warranty
                  </h3>
                  <p className="text-muted-foreground font-sans">
                    We stand behind our craftsmanship. Enjoy peace of mind with a full decade of coverage on all materials and labor for your new seamless gutter system.
                  </p>
                </div>
              </CardContent>
            </Card>

            {}
            <div>
              <h3 className="text-xl font-bold font-default text-foreground mb-4">
                Recent Transformations
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {galleryImages.map((image, i) => <div key={image.id} data-index={i} className="relative group overflow-hidden rounded-lg shadow-sm border border-border/50">
                    <AspectRatio ratio={4 / 3}>
                      <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    </AspectRatio>
                    <div className="absolute bottom-2 left-2">
                      <Badge variant={image.label === "Before" ? "outline" : "secondary"} className="bg-background/90 backdrop-blur-sm shadow-sm">
                        {image.label}
                      </Badge>
                    </div>
                  </div>)}
              </div>
            </div>

            {}
            <Card className="bg-card shadow-md border-border/50 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-5">
                <Quote className="size-24" />
              </div>
              
            </Card>

          </div>
        </div>
      </div>
    </section>;
}